import { css } from '@emotion/react';
import {
  type FC, memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { Accordion } from '~/_shared/baseComponents/accordion';
import { LottieAnimationTypes } from '~/_shared/baseComponents/lottieAnimation';
import { useLottieAnimationDefaultColors } from '~/_shared/baseComponents/lottieAnimation/useLottieAnimationDefaultColors';
import { OutlinePanelComponent } from '~/_shared/baseComponents/outlinePanel/outlinePanel.component';
import { OutlinePanelOptions } from '~/_shared/baseComponents/outlinePanel/outlinePanel.constants';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLottieAnimationComponent } from '~/_shared/components/overlay/overlayLottieAnimation.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { useHookWithRefCallback } from '~/_shared/utils/hooks/useHookWithRefCallback';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import {
  INITIAL_CONNECTIVITY_SETTINGS, LayeredMapModalSection,
} from '~/map/layered/createLayeredMapModal/createLayeredMapModal.constants';
import { type MapListingItem } from '~/map/listing/item/mapListingItem';
import type { ModalProps } from '~/modal/modalType.enum';
import { type MapIntersect } from '../../map.repository';
import { LAYERED_MAP_MODAL_MAX_WIDTH } from '../layeredMap.constants';
import { LayeredMapNameAndDescriptionComponent } from '../layeredMapNameAndDescription.component';
import { type MapConnectSettings } from '../mapConnectSettings';
import { ConnectivitySettingsSectionComponent } from './ConnectivitySettingsSection/connectivitySettingsSection.component';
import { MatchupColumnsSectionContainer } from './MatchupColumnsSection/matchupColumnsSection.container';
import {
  SelectMapAddOrRemoveOperation, type SelectMapsAddBaseMapProps,
} from './SelectMapsSection/selectMapsSection.component';
import { SelectMapsSectionContainer } from './SelectMapsSection/selectMapsSection.container';

const itemButtonStyle = css({
  textTransform: 'uppercase',
});

const overlayAnimationStyle = css({
  position: 'fixed',
});

const columnsSelectHeadingStyle = css({
  margin: '0 0 8px',
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.2,
});

export type CreateLayeredMapModalSettings = {
  connectionSetting: MapConnectSettings | null;
  mapDescription?: string;
  mapName: string;
  matches: MapIntersect[];
  selectedMapListItems: MapListingItem[];
};

const isStepExpanded = (currentStep: LayeredMapModalSection) => ({
  [LayeredMapModalSection.Name]: currentStep === LayeredMapModalSection.Name,
  [LayeredMapModalSection.ConnectivitySettings]: currentStep === LayeredMapModalSection.ConnectivitySettings,
  [LayeredMapModalSection.MapsSelection]: currentStep === LayeredMapModalSection.MapsSelection,
  [LayeredMapModalSection.MatchUp]: currentStep === LayeredMapModalSection.MatchUp,
});

const isStepLocked = (currentStep: LayeredMapModalSection) => ({
  [LayeredMapModalSection.Name]: false,
  [LayeredMapModalSection.ConnectivitySettings]: currentStep < LayeredMapModalSection.ConnectivitySettings,
  [LayeredMapModalSection.MapsSelection]: currentStep < LayeredMapModalSection.MapsSelection,
  [LayeredMapModalSection.MatchUp]: currentStep < LayeredMapModalSection.MatchUp,
});

export type CreateLayeredMapComponentProps = Readonly<{
  connectionSetting: MapConnectSettings;
  currentStep: LayeredMapModalSection;
  isCreatingLayeredMap: boolean;
  isLoading: boolean;
  layerAnimationColors?: Partial<Record<string, string>>;
  mapDescription: string;
  mapName: string;
  selectedMapListItems: Array<MapListingItem>;
  selectedMaps: Array<MapListingItem | null>;

  onConnectionSettingChange: (setting: MapConnectSettings) => void;
  onConnectivitySettingsContinue: () => void;
  onCreateLayeredMapSubmit: (matches: MapIntersect[]) => void;
  onGoToStep: (step: LayeredMapModalSection) => void;
  onIsLoadingChange: (isLoading: boolean) => void;
  onMapDescriptionChange: (description: string) => void;
  onMapNameChange: (name: string) => void;
  onMapOverviewContinue: () => void;
  onMapSelected: (map: MapListingItem, mapIndex: number) => void;
  onSelectMapSubmit: () => void;
  onSelectedMapsChange: (maps: Array<MapListingItem | null>) => void;
  setMapNameInputRef: (ref: HTMLInputElement | null) => void;
}>;

export const CreateLayeredMapComponent: FC<CreateLayeredMapComponentProps> = memo(({
  connectionSetting,
  currentStep,
  isCreatingLayeredMap,
  isLoading,
  layerAnimationColors,
  mapDescription,
  mapName,
  selectedMapListItems,
  selectedMaps,
  onConnectionSettingChange,
  onConnectivitySettingsContinue,
  onCreateLayeredMapSubmit,
  onGoToStep,
  onIsLoadingChange,
  onMapDescriptionChange,
  onMapNameChange,
  onMapOverviewContinue,
  onMapSelected,
  onSelectMapSubmit,
  onSelectedMapsChange,
  setMapNameInputRef,
}) => {
  const expandedPerStep = useMemo(() => isStepExpanded(currentStep), [currentStep]);
  const lockedPerStep = useMemo(() => isStepLocked(currentStep), [currentStep]);
  const [t] = useTranslation();

  const isMapOverviewContinueEnabled = !isTextEmpty(mapName);
  const isSelectMapContinueDisabled = selectedMaps.some(isNullsy);

  return (
    <>
      <OutlinePanelComponent outlines={[OutlinePanelOptions.Top]}>
        <Accordion>
          <Accordion.Item
            open={expandedPerStep[LayeredMapModalSection.Name]}
            isLocked={lockedPerStep[LayeredMapModalSection.Name]}
            onToggle={() => onGoToStep(LayeredMapModalSection.Name)}
          >
            <Accordion.ItemHeading css={itemButtonStyle}>
              <Accordion.StepNumber />
              {t('Name Your Map')}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <LayeredMapNameAndDescriptionComponent
                isContinueEnabled={isMapOverviewContinueEnabled}
                mapDescription={mapDescription}
                mapName={mapName}
                onContinue={onMapOverviewContinue}
                setMapDescription={onMapDescriptionChange}
                setMapName={onMapNameChange}
                setMapNameInputRef={setMapNameInputRef}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item
            open={expandedPerStep[LayeredMapModalSection.ConnectivitySettings]}
            isLocked={lockedPerStep[LayeredMapModalSection.ConnectivitySettings]}
            onToggle={() => onGoToStep(LayeredMapModalSection.ConnectivitySettings)}
          >
            <Accordion.ItemHeading css={itemButtonStyle}>
              <Accordion.StepNumber />
              {t('Connectivity Settings')}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <ConnectivitySettingsSectionComponent
                onChange={onConnectionSettingChange}
                selected={connectionSetting}
                onContinue={onConnectivitySettingsContinue}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item
            open={expandedPerStep[LayeredMapModalSection.MapsSelection]}
            isLocked={lockedPerStep[LayeredMapModalSection.MapsSelection]}
            onToggle={() => onGoToStep(LayeredMapModalSection.MapsSelection)}
          >
            <Accordion.ItemHeading css={itemButtonStyle}>
              <Accordion.StepNumber />
              {t('Select Maps to Layer')}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              {expandedPerStep[LayeredMapModalSection.MapsSelection] && (
                <SelectMapsSectionContainer
                  onMapSelected={onMapSelected}
                  selectedMaps={selectedMaps}
                  selectMapAddOrRemove={ADD_BASE_MAP_PROPS}
                  isDisabled={isSelectMapContinueDisabled}
                  onSubmit={onSelectMapSubmit}
                  onChangeAddedMaps={onSelectedMapsChange}
                />
              )}
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item
            open={expandedPerStep[LayeredMapModalSection.MatchUp]}
            isLocked={lockedPerStep[LayeredMapModalSection.MatchUp]}
            onToggle={() => onGoToStep(LayeredMapModalSection.MatchUp)}
          >
            <Accordion.ItemHeading css={itemButtonStyle}>
              <Accordion.StepNumber />
              {t('Match-up Map Columns')}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <p css={columnsSelectHeadingStyle}>
                {t('You\'re almost there! Simply match the corresponding columns and then decide on a name for your layered column. You can either use one of the original column names or come up with a brand-new one.')}
              </p>
              {expandedPerStep[LayeredMapModalSection.MatchUp] && (
                <MatchupColumnsSectionContainer
                  maps={selectedMapListItems}
                  mapName={mapName}
                  onSubmit={onCreateLayeredMapSubmit}
                  setIsLoading={onIsLoadingChange}
                  isSubmitting={isCreatingLayeredMap}
                />
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </OutlinePanelComponent>
      {(isLoading || isCreatingLayeredMap) && (
        <OverlayLottieAnimationComponent
          css={overlayAnimationStyle}
          type={LottieAnimationTypes.Layer}
          size={120}
          autoplay
          loop
          colors={layerAnimationColors}
        />
      )}
    </>
  );
});

type CreateLayeredMapModalComponentProps = ModalProps<{
  initialData?: {
    mapName?: string;
    mapDescription?: string;
  };
  isCreatingLayeredMap: boolean;
  onCreateLayeredMap: (settings: CreateLayeredMapModalSettings) => void;
}>;

export const CreateLayeredMapModalComponent: FC<CreateLayeredMapModalComponentProps> = memo(({
  isOpen,
  onClose,
  isCreatingLayeredMap,
  onCreateLayeredMap,
  ...restProps
}) => {
  const [t] = useTranslation();
  const [currentStep, setCurrentStep] = useState(LayeredMapModalSection.Name);

  const [mapName, setMapName] = useState(restProps.initialData?.mapName ?? '');
  const [mapDescription, setMapDescription] = useState(restProps.initialData?.mapDescription ?? '');
  const [connectionSetting, setConnectionSetting] = useState<MapConnectSettings>(INITIAL_CONNECTIVITY_SETTINGS);
  const [selectedMaps, setSelectedMaps] = useState<Array<MapListingItem | null>>([null, null]);
  const [isLoading, setIsLoading] = useState(false);

  const [mapNameInputRef, setMapNameInputRef] = useHookWithRefCallback<HTMLInputElement>();
  const layerAnimationColors = useLottieAnimationDefaultColors().Layer;

  const selectedMapListItems = useMemo(() => selectedMaps.filter(notNullsy), [selectedMaps]);

  const onMapOverviewContinue = useCallback(() =>
    setCurrentStep(LayeredMapModalSection.ConnectivitySettings),
  []);

  const onConnectivitySettingsContinue = useCallback(() => {
    if (connectionSetting) {
      setCurrentStep(LayeredMapModalSection.MapsSelection);
    }
  }, [connectionSetting]);

  const onMapSelected = useCallback((map: MapListingItem, mapIndex: number) => {
    setSelectedMaps(maps => [...maps.slice(0, mapIndex), map, ...maps.slice(mapIndex + 1)]);
  }, []);

  const onSelectMapSubmit = useCallback(() => {
    setCurrentStep(LayeredMapModalSection.MatchUp);
  }, []);

  const onCreateLayeredMapSubmit = useCallback((matches: MapIntersect[]) => {
    onCreateLayeredMap({ mapName, selectedMapListItems, connectionSetting, matches, mapDescription });
  }, [onCreateLayeredMap, selectedMapListItems, mapName, mapDescription, connectionSetting]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(LayeredMapModalSection.Name);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mapNameInputRef) {
      mapNameInputRef.focus();
    }
  }, [isOpen, mapNameInputRef]);

  return (
    <ModalComponent
      onClose={onClose}
      isOpen={isOpen}
      caption={t('layeredMap.creation.layer')}
      contentStyle={css({ padding: 0 })}
      maxWidth={LAYERED_MAP_MODAL_MAX_WIDTH}
    >
      <CreateLayeredMapComponent
        connectionSetting={connectionSetting}
        currentStep={currentStep}
        isCreatingLayeredMap={isCreatingLayeredMap}
        isLoading={isLoading}
        layerAnimationColors={layerAnimationColors}
        mapDescription={mapDescription}
        mapName={mapName}
        selectedMapListItems={selectedMapListItems}
        selectedMaps={selectedMaps}
        onConnectionSettingChange={setConnectionSetting}
        onConnectivitySettingsContinue={onConnectivitySettingsContinue}
        onCreateLayeredMapSubmit={onCreateLayeredMapSubmit}
        onGoToStep={setCurrentStep}
        onIsLoadingChange={setIsLoading}
        onMapDescriptionChange={setMapDescription}
        onMapNameChange={setMapName}
        onMapOverviewContinue={onMapOverviewContinue}
        onMapSelected={onMapSelected}
        onSelectMapSubmit={onSelectMapSubmit}
        onSelectedMapsChange={setSelectedMaps}
        setMapNameInputRef={setMapNameInputRef}
      />
    </ModalComponent>
  );
});

const ADD_BASE_MAP_PROPS: SelectMapsAddBaseMapProps = {
  operation: SelectMapAddOrRemoveOperation.ADD,
};
